import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  FileText,
  Loader2,
  ChevronDown,
  ChevronUp,
  User,
  Mail,
  Phone,
  Target,
} from "lucide-react";
import Navigation from "./Navigation";
import { clientApi, Client } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";

const categoryLabels: Record<string, string> = {
  'stomach': 'أمراض المعدة',
  'diabetes': 'السكري',
  'cholesterol': 'الكوليسترول',
  'ibs': 'القولون العصبي',
  'blood-pressure': 'ضغط الدم',
  'anemia': 'فقر الدم',
  'kidney': 'أمراض الكلى',
  'custom': 'ملاحظة مخصصة',
};

const categoryColors: Record<string, string> = {
  'stomach': 'bg-orange-100 text-orange-800 border-orange-200',
  'diabetes': 'bg-blue-100 text-blue-800 border-blue-200',
  'cholesterol': 'bg-red-100 text-red-800 border-red-200',
  'ibs': 'bg-purple-100 text-purple-800 border-purple-200',
  'blood-pressure': 'bg-rose-100 text-rose-800 border-rose-200',
  'anemia': 'bg-amber-100 text-amber-800 border-amber-200',
  'kidney': 'bg-teal-100 text-teal-800 border-teal-200',
  'custom': 'bg-gray-100 text-gray-800 border-gray-200',
};

const DoctorAdvice = () => {
  const navigate = useNavigate();
  const { isAuthenticated, clientName, isLoading } = useAuth();
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedNote, setExpandedNote] = useState<number | null>(null);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    if (isLoading || !isAuthenticated) return;

    if (!clientName) {
      setError('No client information found');
      setLoading(false);
      return;
    }

    setLoading(true);
    clientApi.getAll()
      .then(clients => {
        const foundClient = clients.find(c =>
          c.name.trim().toLowerCase() === clientName.trim().toLowerCase()
        );
        if (foundClient) {
          setClient(foundClient);
        } else {
          setError('Client not found');
        }
      })
      .catch(err => {
        console.error('Error fetching client data:', err);
        setError('Failed to load data');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isAuthenticated, clientName, isLoading]);

  const healthNotes = client?.healthNotes || [];
  const sortedNotes = [...healthNotes].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  if (isLoading || loading) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading your advice...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
          <Card className="max-w-md w-full mx-4">
            <CardContent className="pt-6 text-center">
              <p className="text-red-500 mb-2">{error}</p>
              <p className="text-sm text-muted-foreground">Please try logging in again.</p>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Client Info Header */}
          {client && (
            <Card className="mb-8 border-none shadow-lg bg-white">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <img
                    src={client.image}
                    alt={client.name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-primary/20"
                  />
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-bold text-foreground">{client.name}</h2>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
                      {client.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3.5 h-3.5" />
                          {client.email}
                        </span>
                      )}
                      {client.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5" />
                          {client.phone}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {client.goal && (
                        <Badge variant="outline" className="text-xs">
                          <Target className="w-3 h-3 mr-1" />
                          {client.goal}
                        </Badge>
                      )}
                      {client.age && (
                        <Badge variant="outline" className="text-xs">
                          <User className="w-3 h-3 mr-1" />
                          {client.age} years
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Page Title */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Doctor's Advice</h1>
              <p className="text-sm text-muted-foreground">
                {sortedNotes.length} {sortedNotes.length === 1 ? 'note' : 'notes'} from your doctor
              </p>
            </div>
          </div>

          {/* Health Notes */}
          {sortedNotes.length > 0 ? (
            <div className="space-y-4">
              {sortedNotes.map((note, index) => {
                const isExpanded = expandedNote === index;
                return (
                  <Card
                    key={index}
                    className="border-none shadow-md hover:shadow-lg transition-shadow cursor-pointer bg-white"
                    onClick={() => setExpandedNote(isExpanded ? null : index)}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-base font-semibold leading-tight">
                            {note.title}
                          </CardTitle>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {(note.categories || []).map((cat: string) => (
                              <span
                                key={cat}
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${categoryColors[cat] || categoryColors['custom']}`}
                              >
                                {categoryLabels[cat] || cat}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-muted-foreground">
                            {new Date(note.createdAt).toLocaleDateString()}
                          </span>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {isExpanded ? (
                        <div
                          className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed bg-gray-50 rounded-lg p-4 mt-1"
                          dir="rtl"
                        >
                          {note.content}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground line-clamp-2" dir="rtl">
                          {note.content}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="border-none shadow-md bg-white">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground font-medium">No advice yet</p>
                <p className="text-sm text-muted-foreground/70 mt-1">
                  Your doctor hasn't added any health notes for you yet.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};

export default DoctorAdvice;
